import {
	type DateRange,
	InvestmentExportControllerApiFactory,
	type PerformanceExportRequest,
	type PerformanceMetric,
} from "@eu.mdotm.api/sphere";
import { getApiGen } from "$root/api/factory";
import { downloadContentDisposition } from "$root/utils";
import { allowedMetrics, type ComputedMetrics, metricsMap } from "./common";

/**
 * Maps the metrics computed for a single series (`uuid`) into the `{ label, value }` pairs the export
 * endpoint expects. Mirrors exactly what the metrics table renders: only metrics with a computed value
 * are included, and percentage-scale metrics are multiplied by 100 (e.g. `12.34` for `12.34%`) to match
 * the table cells. Iterating `allowedMetrics` keeps the order aligned with the default column order.
 */
export function buildPerformanceMetrics(
	computedMetrics: ComputedMetrics | undefined,
	uuid: string,
): PerformanceMetric[] {
	if (!computedMetrics) {
		return [];
	}
	return allowedMetrics.flatMap((metric) => {
		const raw = computedMetrics[metric]?.[uuid];
		if (raw == null || Number.isNaN(raw)) {
			return [];
		}
		const value = metricsMap[metric].scale === "percentage" ? raw * 100 : raw;
		return [{ label: metricsMap[metric].label, value }];
	});
}

/**
 * Converts the chart's current zoom (milliseconds, UTC — the chart axis uses `useUTC`) into the
 * `{ from, to }` `YYYY-MM-DD` date strings the export endpoint expects.
 */
export function toExportDateRange(range: { min: number; max: number }): DateRange {
	return { from: toIsoDate(range.min), to: toIsoDate(range.max) };
}

function toIsoDate(ms: number): string {
	return new Date(ms).toISOString().slice(0, 10);
}

/** Calls the performance export endpoint with the assembled payload and triggers the file download. */
export async function exportPortfolioPerformance(request: PerformanceExportRequest): Promise<void> {
	const exportApi = getApiGen(InvestmentExportControllerApiFactory);
	const response = await exportApi.exportPerformance(request, { responseType: "blob" });
	await downloadContentDisposition(response);
}
